import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { CalendarDays, MapPin, ChevronRight, Users, Award, Clock } from "lucide-react";
import { parseCalendarDate } from "@/lib/dates";

function SectionHeading({ label }) {
  return (
    <div className="flex items-center gap-4 mb-6">
      <div className="h-px flex-1 bg-gradient-to-r from-bronze/30 to-transparent" />
      <span className="text-[11px] sm:text-xs tracking-[0.25em] uppercase text-bronze font-medium whitespace-nowrap">
        {label}
      </span>
      <div className="h-px flex-1 bg-gradient-to-l from-bronze/30 to-transparent" />
    </div>
  );
}

function EventCard({ event }) {
  const d = parseCalendarDate(event.date);
  return (
    <Link
      to={`/events/${event.id}`}
      className="block group"
      data-testid={`home-event-${event.id}`}
    >
      <div className="bg-walnut border border-lv-border p-5 transition-all duration-300 hover:border-bronze/40">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            {d && (
              <p className="text-[10px] tracking-[0.3em] uppercase text-bronze mb-1.5">
                {d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}
              </p>
            )}
            <h3 className="font-serif text-lg text-cream tracking-wide group-hover:text-sand transition-colors">
              {event.title}
            </h3>
            {event.location && (
              <div className="flex items-center gap-1.5 mt-2">
                <MapPin size={11} className="text-lv-muted/60 shrink-0" />
                <span className="text-[11px] text-lv-muted/70 tracking-wide">{event.location}</span>
              </div>
            )}
          </div>
          <ChevronRight size={16} className="text-lv-muted/40 group-hover:text-bronze transition-colors shrink-0 mt-1" />
        </div>
      </div>
    </Link>
  );
}

function GamePreview({ game }) {
  const d = game.game_datetime ? new Date(game.game_datetime) : null;
  return (
    <Link
      to={`/games/${game.id}`}
      className="block group"
      data-testid={`home-game-${game.id}`}
    >
      <div className="bg-walnut border border-lv-border p-5 transition-all duration-300 hover:border-bronze/40">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            {d && (
              <p className="text-[10px] tracking-[0.3em] uppercase text-bronze mb-1.5">
                {d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}
                {" \u00b7 "}
                {d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}
              </p>
            )}
            <h3 className="font-serif text-lg text-cream tracking-wide group-hover:text-sand transition-colors">
              {game.title}
            </h3>
            {game.location && (
              <div className="flex items-center gap-1.5 mt-2">
                <MapPin size={11} className="text-lv-muted/60 shrink-0" />
                <span className="text-[11px] text-lv-muted/70 tracking-wide">{game.location}</span>
              </div>
            )}
          </div>
          <div className="shrink-0 text-right">
            {game.interest_count > 0 && (
              <span className="text-[10px] tracking-wider text-lv-muted">
                {game.interest_count} interested
              </span>
            )}
            <ChevronRight size={16} className="text-lv-muted/40 group-hover:text-bronze transition-colors mt-1 ml-auto" />
          </div>
        </div>
      </div>
    </Link>
  );
}

function ContentCard({ item }) {
  const TYPE_LABELS = {
    EVENT: "Event",
    GAME: "Game",
    PARTNER_UPDATE: "Partner",
    COMMUNITY_BRIEF: "Community",
    EXPERT_TIP: "Expert Tip",
  };
  return (
    <div
      className="bg-walnut border border-lv-border p-5"
      data-testid={`home-content-${item.id}`}
    >
      <p className="text-[10px] tracking-[0.3em] uppercase text-lv-muted mb-1.5">
        {TYPE_LABELS[item.content_type] || item.content_type}
      </p>
      <h3 className="font-serif text-lg text-cream tracking-wide">{item.title}</h3>
      {item.subtitle && (
        <p className="text-sm text-sand/70 mt-1.5 leading-relaxed">{item.subtitle}</p>
      )}
    </div>
  );
}

export default function Home() {
  const { data, isLoading, error } = useQuery({
    queryKey: ["member-home"],
    queryFn: () => api.get("/member/home"),
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-32" data-testid="home-loading">
        <div className="w-12 h-0.5 bg-bronze mx-auto animate-pulse" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-32 px-6" data-testid="home-error">
        <p className="text-cream text-sm">{error.message}</p>
      </div>
    );
  }

  const {
    preferred_name,
    chapter_name,
    coming_up = [],
    find_a_game = [],
    community_brief = [],
    privileges_summary = {},
  } = data;

  const hasComingUp = coming_up.length > 0;
  const hasGames = find_a_game.length > 0;
  const hasBrief = community_brief.length > 0;

  return (
    <div className="max-w-3xl mx-auto" data-testid="home-page">
      {/* Greeting */}
      <div className="mb-12 sm:mb-16" data-testid="home-greeting">
        <p className="text-[10px] tracking-[0.5em] uppercase text-lv-muted mb-2">
          Your Community, Today
        </p>
        <h1 className="font-serif text-3xl sm:text-4xl text-cream tracking-wide">
          Welcome back, {preferred_name}
        </h1>
        {chapter_name && (
          <p className="text-sm text-lv-muted tracking-wide mt-2">{chapter_name}</p>
        )}
      </div>

      <div className="space-y-12 sm:space-y-16">
        {/* Coming Up */}
        {hasComingUp && (
          <section data-testid="home-coming-up">
            <SectionHeading label="Coming Up" />
            <div className="space-y-3">
              {coming_up.map((e) => (
                <EventCard key={e.id} event={e} />
              ))}
            </div>
            <div className="mt-4 text-right">
              <Link
                to="/events"
                className="text-xs tracking-[0.15em] uppercase text-bronze hover:text-sand transition-colors"
                data-testid="home-see-all-events"
              >
                All Events
                <ChevronRight size={12} className="inline ml-1" />
              </Link>
            </div>
          </section>
        )}

        {/* Find a Game */}
        {hasGames && (
          <section data-testid="home-find-a-game">
            <SectionHeading label="Find a Game" />
            <div className="space-y-3">
              {find_a_game.map((g) => (
                <GamePreview key={g.id} game={g} />
              ))}
            </div>
            <div className="mt-4 text-right">
              <Link
                to="/games"
                className="text-xs tracking-[0.15em] uppercase text-bronze hover:text-sand transition-colors"
                data-testid="home-see-all-games"
              >
                All Games
                <ChevronRight size={12} className="inline ml-1" />
              </Link>
            </div>
          </section>
        )}

        {/* Community Brief */}
        {hasBrief && (
          <section data-testid="home-community-brief">
            <SectionHeading label="Community Brief" />
            <div className="space-y-3">
              {community_brief.map((c) => (
                <ContentCard key={c.id} item={c} />
              ))}
            </div>
          </section>
        )}

        {/* Your Privileges — always shown */}
        <section data-testid="home-privileges">
          <SectionHeading label="Your Privileges" />
          <Link to="/privileges" className="block group" data-testid="home-privileges-link">
            <div className="bg-walnut border border-lv-border p-6 sm:p-8 transition-all duration-300 hover:border-bronze/40">
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-3 mb-3">
                    <Award size={18} className="text-bronze" />
                    <span className="font-serif text-xl text-cream tracking-wide">
                      Privileges Network
                    </span>
                  </div>
                  <p className="text-sm text-sand/70 leading-relaxed">
                    {privileges_summary.partner_count > 0
                      ? `${privileges_summary.partner_count} partner${privileges_summary.partner_count !== 1 ? "s" : ""} \u00b7 ${privileges_summary.total_privileges} privilege${privileges_summary.total_privileges !== 1 ? "s" : ""} available`
                      : "Explore your member privileges"}
                  </p>
                </div>
                <ChevronRight size={20} className="text-lv-muted/40 group-hover:text-bronze transition-colors shrink-0" />
              </div>
            </div>
          </Link>
        </section>
      </div>
    </div>
  );
}
